// Live "engine is working" monitor — pulls from the recorder's
// AnalyserNode and emits two streams of updates:
//   1. fast (~30 Hz) RMS for the level meter
//   2. slow (~2 Hz) HSS-based RPM detection + intensity sample for the
//      dual-line trace
//
// Deliberately separate from the server-side analysis pipeline. The
// purpose here is to give the user immediate "the app understands my
// engine" feedback during recording — not to compete with the
// four-detector consensus used after the clip is uploaded.

export interface LiveEngineConfig {
  numCylinders: number;
  /** 4-stroke = each cylinder fires every 2 revs; 2-stroke = every rev. */
  numStrokes: 2 | 4;
  /** 'engine' = ICE only, 'hybrid' = ICE + electric (EV/engine-mode
   *  badge surfaced), 'ev' = electric only (no firing-rate detection
   *  expected at all). Drives detector gating and UI presentation. */
  powertrainKind: 'engine' | 'hybrid' | 'ev';
}

/** Per-tick classification. Determined by RPM-detector confidence vs
 *  intensity floor with simple hysteresis. */
export type PowertrainMode = 'engine' | 'ev' | 'off' | 'unknown';

export interface LiveTracePoint {
  /** Wall-clock ms at sample. */
  t: number;
  /** RPM in this sample window. null if HSS confidence below threshold
   *  (e.g. engine off, very quiet, or noise-dominated). */
  rpm: number | null;
  /** 0..1 confidence score from HSS (best vs median ratio, squashed). */
  rpmConfidence: number;
  /** Intensity in dBFS (full-scale). Range roughly -80 (silence) to 0
   *  (clipping). The dual-line trace plots this normalized to [0, 1]
   *  via a perceptual mapping. */
  intensityDbfs: number;
  /** Powertrain-mode classification at this tick — already
   *  hysteresis-filtered, so it only changes after a sustained
   *  transition, not on a single noisy frame. */
  mode: PowertrainMode;
}

export type LevelCallback = (rmsDbfs: number, peakDbfs: number) => void;
export type TraceCallback = (point: LiveTracePoint) => void;

const RMS_INTERVAL_MS  = 33;      // ~30 Hz
const RPM_INTERVAL_MS  = 500;     // 2 Hz — RPM detection is the expensive one
const TRACE_RING_MAX   = 600;     // ~5 minutes at 2 Hz; UI usually shows last ~30 s
const RPM_MIN          = 250;
const RPM_MAX          = 4500;
/** Below this HSS confidence we report rpm = null. The trace still
 *  carries the intensity sample so the dotted line stays continuous,
 *  but the RPM line breaks (so the user can see "engine off" vs
 *  "tracking"). */
const RPM_CONF_GATE    = 0.18;
/** Confidence floor for declaring the engine *running* in the
 *  EV/engine-mode classifier. Higher than RPM_CONF_GATE because we
 *  want clean state transitions, not flickering. (TUNE-ME — calibrate
 *  against a real hybrid recording.) */
const MODE_ENGINE_GATE = 0.30;
/** Intensity floor for distinguishing 'ev' (audible motor / road noise
 *  / inverter whine) from 'off' (silence). dBFS — recordings near or
 *  below this are treated as no signal. (TUNE-ME — calibrate against a
 *  cabin-quiet hybrid in EV mode at standstill.) */
const MODE_EV_FLOOR_DB = -55;
/** Number of consecutive ticks the candidate mode must hold before the
 *  classifier flips. With RPM_INTERVAL_MS = 500, 3 ticks ≈ 1.5 s. */
const MODE_HYSTERESIS  = 3;

export class LiveEngineMonitor {
  private analyser: AnalyserNode;
  private cfg: LiveEngineConfig;
  // Buffers must be backed by a plain ArrayBuffer (not SharedArrayBuffer)
  // so they satisfy AnalyserNode's getFloat*Data signatures.
  private timeBuf: Float32Array<ArrayBuffer>;
  private freqBuf: Float32Array<ArrayBuffer>;
  private startedAt = 0;
  private rmsTimer: ReturnType<typeof setInterval> | null = null;
  private rpmTimer: ReturnType<typeof setInterval> | null = null;
  private onLevel: LevelCallback;
  private onTrace: TraceCallback;
  private trace: LiveTracePoint[] = [];
  // Mode-classifier state. `currentMode` is what we publish; `pending`
  // accumulates evidence for a transition.
  private currentMode: PowertrainMode = 'unknown';
  private pendingMode: PowertrainMode = 'unknown';
  private pendingHits = 0;

  constructor(
    analyser: AnalyserNode,
    cfg: LiveEngineConfig,
    onLevel: LevelCallback,
    onTrace: TraceCallback,
  ) {
    this.analyser = analyser;
    this.cfg = cfg;
    this.onLevel = onLevel;
    this.onTrace = onTrace;
    this.timeBuf = new Float32Array(analyser.fftSize);
    this.freqBuf = new Float32Array(analyser.frequencyBinCount);
  }

  start() {
    this.startedAt = Date.now();
    this.trace = [];
    this.currentMode = 'unknown';
    this.pendingMode = 'unknown';
    this.pendingHits = 0;
    this.rmsTimer = setInterval(() => this.tickRms(), RMS_INTERVAL_MS);
    this.rpmTimer = setInterval(() => this.tickRpm(), RPM_INTERVAL_MS);
  }

  stop() {
    if (this.rmsTimer) clearInterval(this.rmsTimer);
    if (this.rpmTimer) clearInterval(this.rpmTimer);
    this.rmsTimer = null;
    this.rpmTimer = null;
  }

  /** Snapshot of the trace ring buffer. The component re-snapshots on
   *  each Trace callback rather than holding a mutable reference. */
  getTrace(): LiveTracePoint[] {
    return this.trace.slice();
  }

  // ---- internals -------------------------------------------------------

  private tickRms() {
    this.analyser.getFloatTimeDomainData(this.timeBuf);
    let sum = 0;
    let peak = 0;
    for (let i = 0; i < this.timeBuf.length; i += 1) {
      const v = this.timeBuf[i];
      sum += v * v;
      const av = Math.abs(v);
      if (av > peak) peak = av;
    }
    const rms = Math.sqrt(sum / this.timeBuf.length);
    const rmsDb = rms > 1e-8 ? 20 * Math.log10(rms) : -160;
    const peakDb = peak > 1e-8 ? 20 * Math.log10(peak) : -160;
    this.onLevel(rmsDb, peakDb);
  }

  private tickRpm() {
    this.analyser.getFloatTimeDomainData(this.timeBuf);
    this.analyser.getFloatFrequencyData(this.freqBuf);

    // Intensity = same RMS as the level meter, but sampled at the slower
    // RPM cadence so the trace's two lines share an x-axis.
    let sum = 0;
    for (let i = 0; i < this.timeBuf.length; i += 1) {
      const v = this.timeBuf[i];
      sum += v * v;
    }
    const rms = Math.sqrt(sum / this.timeBuf.length);
    const intensityDb = rms > 1e-8 ? 20 * Math.log10(rms) : -160;

    const { rpm, confidence } = this.detectRpm();
    const mode = this.classifyMode(confidence, intensityDb);
    const point: LiveTracePoint = {
      t: Date.now() - this.startedAt,
      rpm: confidence >= RPM_CONF_GATE ? rpm : null,
      rpmConfidence: confidence,
      intensityDbfs: intensityDb,
      mode,
    };
    this.trace.push(point);
    if (this.trace.length > TRACE_RING_MAX) {
      this.trace.splice(0, this.trace.length - TRACE_RING_MAX);
    }
    this.onTrace(point);
  }

  /** Classify the current tick into engine | ev | off, with hysteresis
   *  so a single noisy frame doesn't flip the badge. Pure-EV vehicles
   *  always classify as 'ev' (or 'off' below the floor); pure-engine
   *  vehicles never classify as 'ev' (instead they read as 'off' when
   *  the engine isn't running — historically this is a coast or stall,
   *  not an EV transition).
   *
   *  For 'engine' classification we require BOTH high HSS confidence
   *  AND a sane intensity floor — high HSS on near-silence is almost
   *  always spurious noise locking onto a low bin. */
  private classifyMode(rpmConfidence: number, intensityDb: number): PowertrainMode {
    let candidate: PowertrainMode;
    if (this.cfg.powertrainKind === 'ev') {
      candidate = intensityDb >= MODE_EV_FLOOR_DB ? 'ev' : 'off';
    } else if (this.cfg.powertrainKind === 'engine') {
      // ICE-only: engine is either running or off. Don't conflate
      // "engine off coasting" with "EV mode" — that would mislead.
      candidate = (rpmConfidence >= MODE_ENGINE_GATE && intensityDb >= MODE_EV_FLOOR_DB)
        ? 'engine'
        : 'off';
    } else {
      // Hybrid: full three-way classification.
      if (rpmConfidence >= MODE_ENGINE_GATE && intensityDb >= MODE_EV_FLOOR_DB) {
        candidate = 'engine';
      } else if (intensityDb >= MODE_EV_FLOOR_DB) {
        candidate = 'ev';
      } else {
        candidate = 'off';
      }
    }

    if (candidate === this.currentMode) {
      this.pendingMode = candidate;
      this.pendingHits = 0;
      return this.currentMode;
    }
    if (candidate === this.pendingMode) {
      this.pendingHits += 1;
    } else {
      this.pendingMode = candidate;
      this.pendingHits = 1;
    }
    if (this.pendingHits >= MODE_HYSTERESIS || this.currentMode === 'unknown') {
      this.currentMode = candidate;
      this.pendingHits = 0;
    }
    return this.currentMode;
  }

  /** Harmonic-sum spectrum detector over the firing-rate band. Same
   *  family as detect_rpm_hss in unmixStream.m, but scaled down — we
   *  use 4 harmonics, parabolic peak refinement, and confidence from
   *  the best/median ratio of HSS in-band.
   *
   *  Returns { rpm: NaN, confidence: 0 } when the band is empty or
   *  the spectrum is dominated by noise. */
  private detectRpm(): { rpm: number; confidence: number } {
    const fs = this.analyser.context.sampleRate;
    const nfft = this.analyser.fftSize;
    const binHz = fs / nfft;
    const firingPerRev = this.cfg.numStrokes === 2
      ? this.cfg.numCylinders
      : this.cfg.numCylinders / 2;
    const fMin = (RPM_MIN / 60) * firingPerRev;
    const fMax = (RPM_MAX / 60) * firingPerRev;
    const kMin = Math.max(2, Math.floor(fMin / binHz));
    const kMax = Math.min(this.freqBuf.length - 1, Math.ceil(fMax / binHz));
    if (kMax <= kMin + 2) return { rpm: NaN, confidence: 0 };

    // Convert dB → linear magnitude on the fly. Use a small floor so
    // very-quiet bins don't dominate the comparison.
    const magOf = (k: number): number => {
      if (k < 0 || k >= this.freqBuf.length) return 0;
      const db = this.freqBuf[k];
      return db < -160 ? 0 : Math.pow(10, db / 20);
    };

    let bestK = kMin;
    let bestHss = -Infinity;
    const hssVals = new Float32Array(kMax - kMin + 1);
    let valIdx = 0;
    for (let k = kMin; k <= kMax; k += 1) {
      // 4 harmonics, capped at Nyquist. Skip harmonics that fall above
      // the analyser's bin range — they don't add signal but would be
      // truncated to 0 either way.
      let hss = magOf(k);
      const k2 = k * 2; if (k2 < this.freqBuf.length) hss += magOf(k2);
      const k3 = k * 3; if (k3 < this.freqBuf.length) hss += magOf(k3);
      const k4 = k * 4; if (k4 < this.freqBuf.length) hss += magOf(k4);
      hssVals[valIdx] = hss;
      valIdx += 1;
      if (hss > bestHss) { bestHss = hss; bestK = k; }
    }

    // Parabolic refinement around the best bin (HSS curve is smooth
    // enough for sub-bin resolution to be meaningful).
    let kRefined = bestK;
    if (bestK > kMin && bestK < kMax) {
      const yL = magOf(bestK - 1) + magOf((bestK - 1) * 2)
        + magOf((bestK - 1) * 3) + magOf((bestK - 1) * 4);
      const yC = bestHss;
      const yR = magOf(bestK + 1) + magOf((bestK + 1) * 2)
        + magOf((bestK + 1) * 3) + magOf((bestK + 1) * 4);
      const denom = yL - 2 * yC + yR;
      if (Math.abs(denom) > 1e-12) {
        const offset = 0.5 * (yL - yR) / denom;
        if (offset > -1 && offset < 1) kRefined = bestK + offset;
      }
    }
    const fDom = kRefined * binHz;
    const rpm = (fDom / firingPerRev) * 60;

    // Confidence: ratio of best HSS to the median of in-band HSS,
    // squashed via 1 - 1/r. Engine off → median ≈ best (everything is
    // noise) → ratio ≈ 1 → confidence ≈ 0. Strong lock → ratio ≫ 1
    // → confidence → 1. Robust to a single noisy bin.
    const sorted = hssVals.slice(0, valIdx).sort();
    const median = sorted[Math.floor(sorted.length / 2)] || 1e-12;
    const ratio = bestHss / median;
    const confidence = Math.max(0, 1 - 1 / Math.max(ratio, 1));

    return { rpm, confidence };
  }
}

/** Map dBFS to a visual 0..1 level for the meter. -60 dB → 0,
 *  0 dB → 1, with a soft knee so quiet sounds still register. */
export function dbfsToLevel(db: number): number {
  if (db <= -60) return 0;
  if (db >= 0) return 1;
  return (db + 60) / 60;
}
